import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { z } from 'zod';
import { Building2, Check, User, UserPlus, Mail } from 'lucide-react';
import { useSuspenseQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { useAppForm } from '@/lib/form';
import { appFormOptions } from '@/common/form/appFormOptions';
import { requiredInput } from '@/common/form/requiredInput';
import { Button } from '@/common/components/ui/button';
import { Input } from '@/common/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/common/components/ui/select';
import { cn } from '@/lib/utils';
import { supplyPointQueries, supplyPointQueryKeys } from '@/features/supply-points/queries';
import { organizationQueries } from '@/features/organizations/queries';
import { SupplyPointService } from '@/features/supply-points/services/SupplyPointService';

const inviteSchema = z.object({
  user: requiredInput(z.email()),
  role: z.enum(['full', 'write', 'read']),
});

interface CreateSupplyPointStep2Props {
  supplyPointId: number;
  onNext: () => void;
}

export function CreateSupplyPointStep2({ supplyPointId, onNext }: CreateSupplyPointStep2Props) {
  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <div className="px-6 py-5 border-b border-border">
        <h2 className="text-base font-semibold">
          <Trans>Set up access</Trans>
        </h2>
        <p className="text-sm text-muted-foreground mt-0.5">
          <Trans>
            Assign organizations and invite users. Access can be changed at any time in settings.
          </Trans>
        </p>
      </div>

      <div className="px-6 py-6 space-y-6">
        <OrgAccessSection supplyPointId={supplyPointId} />
        <div className="border-t border-border" />
        <InviteUserSection supplyPointId={supplyPointId} />
      </div>

      <div className="flex items-center justify-between px-6 py-4 border-t border-border bg-muted/30">
        <span className="text-xs text-muted-foreground">
          <Trans>Step 2 of 4 · Optional</Trans>
        </span>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={onNext}>
            <Trans>Skip</Trans>
          </Button>
          <Button onClick={onNext}>
            <UserPlus size={15} className="mr-1.5" />
            <Trans>Save and continue</Trans>
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Org access section ───────────────────────────────────────────────────────

function OrgAccessSection({ supplyPointId }: { supplyPointId: number }) {
  const queryClient = useQueryClient();
  const { data: orgs } = useSuspenseQuery(organizationQueries.list());
  const { data: supplyPoint } = useSuspenseQuery(supplyPointQueries.detail(supplyPointId));

  const addMutation = useMutation({
    mutationFn: (partnerId: number) => SupplyPointService.addPartners(supplyPointId, [partnerId]),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: supplyPointQueryKeys.all }),
  });

  const removeMutation = useMutation({
    mutationFn: (partnerId: number) =>
      SupplyPointService.removePartners(supplyPointId, [partnerId]),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: supplyPointQueryKeys.all }),
  });

  const isPending = addMutation.isPending || removeMutation.isPending;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Building2 size={14} className="text-muted-foreground" />
        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          <Trans>Organizations</Trans>
        </span>
      </div>
      <p className="text-sm text-muted-foreground">
        <Trans>
          All members of the selected organization will gain access to this supply point.
        </Trans>
      </p>

      {orgs.length === 0 ? (
        <div className="flex items-center justify-center rounded-xl border border-dashed border-border py-8">
          <p className="text-sm text-muted-foreground">
            <Trans>No organizations yet</Trans>
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {orgs.map((org) => {
            const selected = supplyPoint.partners.some((p) => p.id === org.id);

            const handleToggle = () => {
              if (isPending) return;
              if (selected) {
                removeMutation.mutate(org.id);
              } else {
                addMutation.mutate(org.id);
              }
            };

            return (
              <button
                key={org.id}
                onClick={handleToggle}
                disabled={isPending}
                className={cn(
                  'w-full flex items-center gap-4 rounded-xl border px-4 py-3 text-left transition-colors',
                  selected
                    ? 'border-primary bg-primary/5'
                    : 'border-border bg-background hover:border-muted-foreground/30'
                )}
              >
                <div
                  className={cn(
                    'flex size-5 shrink-0 items-center justify-center rounded border-2 transition-colors',
                    selected ? 'border-primary bg-primary' : 'border-muted-foreground/40'
                  )}
                >
                  {selected && <Check size={12} className="text-primary-foreground" />}
                </div>
                <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted">
                  <Building2 size={14} className="text-muted-foreground" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-foreground truncate">{org.name}</div>
                  {org.city && (
                    <div className="text-xs text-muted-foreground mt-0.5">{org.city}</div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Invite user section ──────────────────────────────────────────────────────

function roleLabel(role: string): string {
  if (role === 'full') return t`Admin`;
  if (role === 'write') return t`Editor`;
  return t`Reader`;
}

function InviteUserSection({ supplyPointId }: { supplyPointId: number }) {
  const queryClient = useQueryClient();

  const { data: accesses } = useSuspenseQuery(
    supplyPointQueries.accessBySupplyPoint(supplyPointId)
  );

  const form = useAppForm(
    appFormOptions({
      schema: inviteSchema,
      defaultValues: { user: null, role: 'read' as const },
      onSubmit: async ({ value, formApi }) => {
        await SupplyPointService.createAccess(supplyPointId, {
          user: value.user,
          role: value.role,
        });
        await queryClient.invalidateQueries({ queryKey: supplyPointQueryKeys.all });
        formApi.reset();
      },
    })
  );

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <UserPlus size={14} className="text-muted-foreground" />
        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          <Trans>Invite users</Trans>
        </span>
      </div>
      <p className="text-sm text-muted-foreground">
        <Trans>
          Invite individual users by email. They will receive an invitation to this supply point.
        </Trans>
      </p>

      {/* Inline invite form */}
      <form.AppForm>
        <form.FormElement>
          <div className="flex gap-2">
            <form.AppField name="user">
              {(field) => (
                <div className="relative flex-1">
                  <Mail
                    size={13}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                  />
                  <Input
                    type="email"
                    id={field.name}
                    placeholder={t`user@company.com`}
                    value={field.state.value ?? ''}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    className="pl-8"
                  />
                </div>
              )}
            </form.AppField>
            <form.AppField name="role">
              {(field) => (
                <Select
                  value={field.state.value}
                  onValueChange={(v) => field.handleChange(v as 'full' | 'write' | 'read')}
                >
                  <SelectTrigger className="w-32 shrink-0">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full">
                      <Trans>Admin</Trans>
                    </SelectItem>
                    <SelectItem value="write">
                      <Trans>Editor</Trans>
                    </SelectItem>
                    <SelectItem value="read">
                      <Trans>Reader</Trans>
                    </SelectItem>
                  </SelectContent>
                </Select>
              )}
            </form.AppField>
            <form.Subscribe selector={(s) => s.isSubmitting}>
              {(isSubmitting) => (
                <Button
                  type="submit"
                  variant="outline"
                  disabled={isSubmitting}
                  className="shrink-0"
                >
                  <Trans>Invite</Trans>
                </Button>
              )}
            </form.Subscribe>
          </div>
        </form.FormElement>
      </form.AppForm>

      {/* Current access list */}
      {accesses.length > 0 && (
        <div className="rounded-xl border border-border overflow-hidden bg-background">
          {accesses.map((access, idx) => (
            <div
              key={access.id}
              className={cn(
                'flex items-center gap-3 px-4 py-3',
                idx < accesses.length - 1 ? 'border-b border-border' : ''
              )}
            >
              <div className="flex size-7 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-semibold shrink-0">
                {(access.user.firstName?.[0] ?? access.user.email[0]).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">
                  {access.user.firstName && access.user.lastName
                    ? `${access.user.firstName} ${access.user.lastName}`
                    : access.user.email}
                </div>
                <div className="text-xs text-muted-foreground truncate">{access.user.email}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-xs text-muted-foreground">{roleLabel(access.role)}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {accesses.length === 0 && (
        <div className="flex items-center gap-2 py-2 text-sm text-muted-foreground">
          <User size={14} className="shrink-0" />
          <Trans>No users invited yet.</Trans>
        </div>
      )}
    </div>
  );
}
